import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Skeleton } from "antd";
import CgIcon from "../common/CgIcon";
import { UiButton } from "../../ui";
import MedicineFormModal from "../Medicines/MedicineFormModal";
import ScanInvoiceModal from "../PurchaseEntry/ScanInvoiceModal";
import {
  Board,
  HeroBand,
  BoardCard,
  BoardColumns,
  BoardColumn,
  BoardEmpty,
} from "./BoardParts";
import { queueIcon } from "./boardIcons";
import { getPharmacyDashboardApi } from "../../../services/pharmacyDashboard.services";
import { formatMoney } from "../common/money";
import styles from "./pharmacyBoard.module.css";

/* Whole rupees on the board - the paise are noise at a glance. */
const inr0 = (value) =>
  formatMoney(Math.round(Number(value) || 0)).replace(".00", "");

/*
 * The Needs Attention rows, in the order the counter works through them. The
 * key is the field the dashboard endpoint returns; the tone, icon and
 * destination are the board's own.
 */
const QUEUES = [
  {
    key: "orders_to_bill",
    label: "Orders to bill",
    note: "raised but not yet billed",
    icon: "orders",
    tone: "#EAAA5E",
    to: "/pharmacy/orders",
  },
  {
    key: "bills_outstanding",
    label: "Bills outstanding",
    note: "money not yet collected",
    icon: "rupee",
    tone: "#F04438",
    to: "/pharmacy/billing",
  },
  {
    key: "sales_returns_to_approve",
    label: "Sales returns to approve",
    note: "waiting on a decision",
    icon: "undo",
    tone: "#9B8AFB",
    to: "/pharmacy/returns",
  },
  {
    key: "purchase_orders_awaiting_approval",
    label: "POs awaiting approval",
    note: "raised, not yet approved",
    icon: "package",
    tone: "#EAAA5E",
    to: "/pharmacy/purchase-order",
  },
  {
    key: "purchase_orders_to_receive",
    label: "POs ready to receive",
    note: "approved, stock not in",
    icon: "cart",
    tone: "#3b6ef6",
    to: "/pharmacy/purchase-entry",
  },
  {
    key: "purchase_returns_to_approve",
    label: "Purchase returns to approve",
    note: "going back to a vendor",
    icon: "package",
    tone: "#9B8AFB",
    to: "/pharmacy/purchase-return",
  },
  {
    key: "medicines_to_reorder",
    label: "Medicines to re-order",
    note: "at or below re-order level",
    icon: "stock",
    tone: "#F04438",
    to: "/pharmacy/stock-out",
  },
];


const QUICK_ACTIONS = [
  { key: "invoice", label: "Upload Invoice", icon: <CgIcon name="upload" size={17} stroke={2} />, modal: "invoice" },
  { key: "po", label: "Raise Purchase Order", icon: <CgIcon name="clipcheck" size={17} stroke={2} />, to: "/pharmacy/purchase-order/new" },
  { key: "preturn", label: "Raise Purchase Return", icon: <CgIcon name="parcelback" size={17} stroke={2} />, to: "/pharmacy/purchase-return/new" },
  { key: "return", label: "Sales Return", icon: <CgIcon name="undo" size={17} stroke={2} />, to: "/pharmacy/returns" },
];

const PharmacyDashboard = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addMedicineOpen, setAddMedicineOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    getPharmacyDashboardApi()
      .then((res) => setBoard(res?.data || null))
      .catch(() => setBoard(null))
      .finally(() => setLoading(false));
  }, []);

  const banner = board?.banner || {};
  const attention = board?.needs_attention || {};

  const queues = useMemo(
    () =>
      QUEUES.map((q) => ({
        ...q,
        count: Number(attention[q.key]?.count) || 0,
        // medicines_to_reorder has no value, and the API sends null for it
        value: Number(attention[q.key]?.value) || 0,
      })),
    [attention]
  );

  const sellers = (board?.top_selling || []).slice(0, 5);
  const bills = (board?.recent_bills || []).slice(0, 4);
  const topUnits = Number(sellers[0]?.units) || 1;

  const pending = Number(banner.pending_orders) || 0;
  const lowStock = Number(banner.low_stock) || 0;
  const nearExpiry = Number(banner.near_expiry) || 0;
  const queueTotal = queues.reduce((sum, q) => sum + q.count, 0);

  /*
   * The next order at the counter, named in the sub-line so the banner says
   * what to do next rather than only how much there is to do.
   */
  const next = banner.next_order;
  const subLine = [
    next
      ? `Next is ${next.order_number}${
          next.patient_name ? ` for ${next.patient_name}` : ""
        }`
      : null,
    `${lowStock} ${lowStock === 1 ? "medicine" : "medicines"} at re-order level`,
    `${nearExpiry} nearing expiry`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Board>
      <HeroBand
        kicker={dayjs(banner.date || undefined).format("dddd, D MMMM")}
        headline={
          pending
            ? `${pending} ${pending === 1 ? "order" : "orders"} waiting to be billed`
            : "Nothing waiting at the counter"
        }
        sub={subLine}
        actions={[
          {
            label: "Add Medicine",
            icon: <CgIcon name="plus" size={16} stroke={2.2} />,
            solid: true,
            // Opens the drawer the Medicines tab already uses - unchanged.
            onClick: () => setAddMedicineOpen(true),
          },
          {
            label: "Purchase Entry",
            icon: <CgIcon name="truck" size={16} stroke={2} />,
            onClick: () => navigate("/pharmacy/purchase-entry/new"),
          },
          {
            label: "New Order",
            icon: <CgIcon name="forms" size={16} stroke={2} />,
            onClick: () => navigate("/pharmacy/orders/sale-entry/new"),
          },
          /*
           * The fourth is a way out of the board rather than a thing to start:
           * the three before it begin a piece of work, this one goes and reads
           * what the work added up to. It sits with them because the band is
           * where the day is picked up from, and "how are we doing" is one of
           * the things picked up there.
           */
          {
            label: "Analytics",
            icon: <CgIcon name="chart" size={16} stroke={2} />,
            onClick: () => navigate("/pharmacy/analytics"),
          },
        ]}
        /*
         * Each figure is a link into the list it counts, which is where anyone
         * reading it wants to go next.
         */
        metrics={[
          {
            label: "Pending Orders",
            value: pending,
            onClick: () => navigate("/pharmacy/orders"),
          },
          {
            label: "Low Stock",
            value: lowStock,
            onClick: () => navigate("/pharmacy/stock-out"),
          },
          {
            label: "Near Expiry",
            value: nearExpiry,
            onClick: () => navigate("/pharmacy/stock-out?tab=near-expiry"),
          },
        ]}
      />

      {/*
        * Two columns rather than two rows: the queue and top sellers down the
        * left, the actions and recent bills down the right. As rows, the short
        * Quick Actions card left a band of empty page above Recent Bills.
        */}
      <BoardColumns>
        <BoardColumn>
          <BoardCard title="Needs Attention" count={loading ? null : queueTotal}>
            <div className={styles.queueList}>
              {queues.map((q) => (
                <button
                  key={q.key}
                  type="button"
                  className={`${styles.queueRow} ${q.count ? "" : styles.clear}`}
                  onClick={() => navigate(q.to)}
                >
                  <span className={styles.qIco} style={{ "--a": q.tone }}>
                    {queueIcon(q.icon)}
                  </span>
                  <span className={styles.qMain}>
                    <b>{q.label}</b>
                    <i>{q.note}</i>
                  </span>
                  <span className={`${styles.qN} ${q.count ? "" : styles.zero}`}>
                    {q.count}
                  </span>
                  <span className={styles.qVal}>{q.value ? inr0(q.value) : "—"}</span>
                  <span className={styles.qGo}>
                    <CgIcon name="chev" size={14} stroke={2.4} />
                  </span>
                </button>
              ))}
            </div>
          </BoardCard>

          <BoardCard
            title="Top Selling Medicines"
            count={loading ? null : sellers.length}
          >
            {loading ? (
              <div style={{ padding: 16 }}>
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
            ) : sellers.length ? (
              <div>
                {sellers.map((med, index) => (
                  <div className={styles.sellRow} key={med.id || index}>
                    <span className={styles.sellRank}>{index + 1}</span>
                    <span className={styles.sellName}>{med.name}</span>
                    <span className={styles.sellBar}>
                      <i
                        style={{
                          width: `${Math.round(
                            ((Number(med.units) || 0) / topUnits) * 100
                          )}%`,
                        }}
                      />
                    </span>
                    <span className={styles.sellN}>
                      {Math.round(Number(med.units) || 0)} units
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <BoardEmpty>No sales this month yet</BoardEmpty>
            )}
          </BoardCard>
        </BoardColumn>

        <BoardColumn>
          <BoardCard title="Quick Actions">
            <div className={styles.qaList}>
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className={styles.qa}
                  onClick={() =>
                    action.modal ? setScanOpen(true) : navigate(action.to)
                  }
                >
                  <span className={styles.qaIco}>{action.icon}</span>
                  <span className={styles.qaT}>{action.label}</span>
                  <span className={styles.qaGo}>
                    <CgIcon name="chev" size={14} stroke={2.4} />
                  </span>
                </button>
              ))}
            </div>
          </BoardCard>

          <BoardCard title="Recent Bills" count={loading ? null : bills.length}>
            {loading ? (
              <div style={{ padding: 16 }}>
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
            ) : bills.length ? (
              <div>
                {bills.map((bill) => (
                  <div
                    className={styles.phRow}
                    key={bill.id}
                    style={{ "--k": "var(--b-primary)" }}
                  >
                    <span className={styles.rxIco}><CgIcon name="rupee2" size={15} stroke={2} /></span>
                    <span className={styles.phMain}>
                      <b>{bill.patient_name || "-"}</b>
                      <i>
                        {bill.bill_number}
                        {bill.sale_type ? ` · ${bill.sale_type}` : ""}
                      </i>
                    </span>
                    <span className={styles.phFig}>{inr0(bill.grand_total)}</span>
                    <span className={styles.phAct}>
                      <UiButton
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/pharmacy/billing/${bill.id}`)}
                      >
                        View
                      </UiButton>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <BoardEmpty>No bills generated yet</BoardEmpty>
            )}
          </BoardCard>
        </BoardColumn>
      </BoardColumns>

      {addMedicineOpen && (
        <MedicineFormModal
          open={addMedicineOpen}
          setOpen={setAddMedicineOpen}
          editData={null}
          refreshPage={() => {}}
        />
      )}

      {scanOpen && (
        <ScanInvoiceModal
          open={scanOpen}
          setOpen={setScanOpen}
          /*
           * Scanning from the board hands the result to Purchase Entry, which
           * is where the lines are edited and saved - same screen as scanning
           * from the Purchase Entry tab.
           */
          onUse={(result) =>
            navigate("/pharmacy/purchase-entry/new", { state: { scan: result } })
          }
        />
      )}
    </Board>
  );
};

export default PharmacyDashboard;
